import { useAuth } from '@/contexts/AuthContext';
import { useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useShop } from '@/contexts/ShopContext';
import { api } from '@/lib/api';

/**
 * Permission matrix mirroring backend PermissionService::ROLE_PERMISSIONS.
 * Keep in sync with apps/api/app/Modules/HRM/Services/PermissionService.php
 */
const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  owner: [
    'products.*',
    'sales.*',
    'stock.*',
    'employees.*',
    'reports.*',
    'settings.*',
    'customers.*',
    'categories.*',
    'brands.*',
    'suppliers.*',
    'warehouses.*',
    'subscriptions.view',
    'returns.*',
    'rental.*',
    'accounting.*',
  ],
  manager: [
    'products.*',
    'sales.*',
    'stock.*',
    'employees.*',
    'reports.*',
    'settings.*',
    'customers.*',
    'categories.*',
    'brands.*',
    'suppliers.*',
    'warehouses.*',
    'subscriptions.view',
    'returns.view',
    'returns.create',
    'rental.*',
  ],
  cashier: [
    'sales.create',
    'sales.view',
    'products.view',
    'customers.*',
    'categories.view',
    'brands.view',
    'stock.view',
    'rental.units',
    'rental.reservations',
    'rental.stays',
    'rental.finance',
  ],
  stockist: [
    'stock.*',
    'products.view',
    'categories.view',
    'brands.view',
    'suppliers.view',
    'warehouses.view',
    'returns.view',
  ],
  accountant: [
    'accounting.read',
    'accounting.capture',
    'accounting.reconcile',
    'accounting.export',
  ],
};

const PERMISSIONS_CACHE_KEY_PREFIX = 'permissions_matrix';

interface PermissionMatrixPayload {
  matrix: Record<string, string[]>;
  version: string;
  updated_at: string;
}

interface AccountingAccessPayload {
  enabled: boolean;
  eligible: boolean;
  preview: boolean;
  read_only: boolean;
  is_owner: boolean;
  permissions: string[];
}

function loadCachedMatrix(shopId: string): Record<string, string[]> | null {
  try {
    const raw = localStorage.getItem(`${PERMISSIONS_CACHE_KEY_PREFIX}:${shopId}`);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PermissionMatrixPayload;
    if (!parsed || !parsed.matrix || typeof parsed.matrix !== 'object') {
      return null;
    }

    return parsed.matrix;
  } catch {
    return null;
  }
}

function saveCachedMatrix(shopId: string, payload: PermissionMatrixPayload): void {
  try {
    localStorage.setItem(`${PERMISSIONS_CACHE_KEY_PREFIX}:${shopId}`, JSON.stringify(payload));
  } catch {
    // Ignore storage failures.
  }
}

function hasPermission(
  role: string,
  permission: string,
  permissionMatrix: Record<string, string[]>,
): boolean {
  const permissions = permissionMatrix[role] ?? [];

  for (const granted of permissions) {
    if (granted === permission) return true;

    const [grantedModule, grantedAction] = granted.split('.');
    const [permModule] = permission.split('.');

    if (grantedModule === permModule && grantedAction === '*') return true;
  }

  return false;
}

export function usePermissions() {
  const { currentShop } = useShop();
  let user: { id: string } | null = null;
  try {
     
    const auth = useAuth();
    user = auth?.user ?? null;
  } catch {
    user = null;
  }
  const queryClient = useQueryClient();
  useEffect(() => {
    const clear = () => {
      queryClient.setQueriesData({ queryKey: ['permissions', 'me'] }, null);
    };
    window.addEventListener('permissions-changed', clear);
    window.addEventListener('offline', clear);
    return () => {
      window.removeEventListener('permissions-changed', clear);
      window.removeEventListener('offline', clear);
    };
  }, [queryClient]);
  const shopId = currentShop?.id ?? '';
  const baseRole = currentShop?.role ?? '';
  const { data: effective } = useQuery({
    queryKey: ['permissions', 'me', shopId, user?.id],
    enabled: Boolean(shopId && user?.id),
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: 30_000,
    queryFn: async () => {
      try {
        return (await api.get<{ role: string; permissions: string[] }>('/permissions/me')).data;
      } catch {
        return null;
      }
    },
  });
  const role = effective?.role ?? baseRole;

  const { data: permissionMatrix = DEFAULT_ROLE_PERMISSIONS } = useQuery({
    queryKey: ['permissions', 'matrix', shopId],
    enabled: Boolean(shopId),
    staleTime: 5 * 60 * 1000,
    refetchOnReconnect: true,
    refetchInterval: 60_000,
    queryFn: async () => {
      try {
        const response = await api.get<PermissionMatrixPayload>('/permissions/matrix');
        const payload = response.data;
        saveCachedMatrix(shopId, payload);

        return payload.matrix;
      } catch {
        return loadCachedMatrix(shopId) ?? DEFAULT_ROLE_PERMISSIONS;
      }
    },
    initialData: () => (shopId ? loadCachedMatrix(shopId) : null) ?? DEFAULT_ROLE_PERMISSIONS,
  });

  const { data: accountingAccess } = useQuery({
    queryKey: ['accounting', shopId, 'access'],
    enabled: Boolean(shopId),
    staleTime: 60_000,
    refetchOnReconnect: true,
    queryFn: async () => (await api.get<AccountingAccessPayload>('/accounting/status')).data,
    retry: false,
  });

  return useMemo(() => {
    const can = (permission: string) => {
      if (permission === 'finances.access') {
        return (
          Boolean(accountingAccess?.permissions.includes('read')) ||
          hasPermission(role, 'reports.view', permissionMatrix)
        );
      }
      if (permission.startsWith('accounting.')) {
        const action = permission.split('.')[1];
        return action === '*'
          ? accountingAccess?.permissions.length === 7
          : Boolean(action && accountingAccess?.permissions.includes(action));
      }
      return hasPermission(
        role,
        permission,
        effective ? { [role]: effective.permissions } : permissionMatrix,
      );
    };
    const canView = (module: string) => can(`${module}.view`) || can(`${module}.*`);
    const canManage = (module: string) => can(`${module}.manage`) || can(`${module}.*`);
    const canCreate = (module: string) => can(`${module}.create`) || can(`${module}.*`);

    return {
      role,
      can,
      canView,
      canManage,
      canCreate,
      isOwner: role === 'owner',
      isManager: role === 'manager',
      isOwnerOrManager: role === 'owner' || role === 'manager',
      isCashier: role === 'cashier',
      isStockist: role === 'stockist',
      isAccountant: role === 'accountant',
    };
  }, [accountingAccess?.permissions, permissionMatrix, role, effective]);
}
